import { mapActions, mapGetters } from 'vuex';
import 'details-element-polyfill';
import Gallery from '@/containers/Gallery/index.vue';
import ProductInfo from '@/containers/ProductInfo/index.vue';
import Breadcrumbs from '@/components/Breadcrumbs/index.vue';
import ProductViewer from '@/components/ProductViewer/index.vue';
import FlashMessage from '@/containers/FlashMessages/index.vue';
import Subnav from '@/components/Subnav/index.vue';
import PcRenderPromo from '@garmin/components-product/src/components/PcRenderPromo';
import breakpoints from '@/helpers/breakpoints';

import Interstitial from '@/containers/Interstitial/index.vue';

export default {
  name: 'App',
  data() {
    return {
      initialLoad: true,
      initialHash: '',
      defaultActiveTab: '',
      showDesktopBanner: true,
    };
  },
  computed: {
    ...mapGetters([
      'getActiveProductTabs',
      'getPartNumber',
      'getProductActiveTabId',
      'getDefaultProductTab',
      'getIsActiveProductTab',
      'getLocale',
      'getStoreCode',
      'getProductId',
      'getBreadCrumbs',
      'getDefaultSku',
      'getPromoBanner',
      'getInterstitialShouldDisplay',
      'getInterstitialIsVisible',
      'getInterstitialProductDescription',
      'getInterstitialProductImage',
      'getInterstitialProductAltText',
      'getViewerOpen',
      'getProductSpinnerImagesBySku',
    ]),
    subNavLabels() {
      return this.getActiveProductTabs.map((product) => product.title);
    },
    subNavContent() {
      return this.getActiveProductTabs.map((product) => product.content);
    },
    subNavIds() {
      return this.getActiveProductTabs.map((product) => product.id);
    },
    showMobileBanner() {
      return !this.showDesktopBanner && this.getPromoBanner;
    },
    showInterstitial() {
      return this.getInterstitialShouldDisplay && this.getInterstitialIsVisible;
    },
    showMainPage() {
      return !this.showInterstitial
        && !this.getViewerOpen;
    },
  },
  mounted() {
    this.setHistoryListener();
  },
  methods: {
    ...mapActions([
      'setBootstrap',
      'updateProductData',
      'setProductActiveTabId',
      'checkCustomerGroupExpiration',
      'setViewerOpen',
    ]),
    initialUpdate() {
      const { params } = this.$router.currentRoute;
      const newParams = {
        locale: this.getLocale,
        storeCode: this.getStoreCode,
        productId: this.getProductId,
        partNumber: this.getPartNumber,
      };

      if (
        (
          params.productId === newParams.productId
          && params.locale === newParams.locale
          && params.storeCode === newParams.storeCode
          && params.partNumber === newParams.partNumber
        )
        || (
          params.productId === newParams.productId
          && params.locale === newParams.locale
          && params.storeCode === newParams.storeCode
          && params.partNumber === undefined
        )
        || (
          params.productId === undefined
          && params.locale === newParams.locale
          && params.storeCode === newParams.storeCode
          && params.partNumber === newParams.partNumber
        )
      ) {
        return;
      }
      if (params.partNumber === undefined) {
        newParams.partNumber = undefined;
        this.$router.push({
          name: 'ShortPath',
          params: newParams,
          hash: this.$router.currentRoute.hash,
        });
      } else if (params.productId === undefined) {
        newParams.productId = undefined;
        this.$router.push({
          name: 'FullPath',
          params: newParams,
          hash: this.$router.currentRoute.hash,
        });
      } else {
        this.$router.push({
          name: 'FullPath',
          params: newParams,
          hash: this.$router.currentRoute.hash,
        });
      }
    },
    updateProductRoute() {
      const { params } = this.$router.currentRoute;
      const newParams = {
        locale: this.getLocale,
        storeCode: this.getStoreCode,
        productId: this.getProductId,
        partNumber: this.getPartNumber,
      };

      if (
        params.productId === newParams.productId
        && params.locale === newParams.locale
        && params.storeCode === newParams.storeCode
        && params.partNumber === newParams.partNumber
      ) {
        return;
      }
      this.$router.push({
        name: 'FullPath',
        params: newParams,
        hash: this.$router.currentRoute.hash,
      });
    },
    setHistoryListener() {
      window.addEventListener('popstate', () => {
        this.updateHistoryProductStates();
      });
    },
    updateHistoryProductStates() {
      const prevHash = this.getProductActiveTabId;
      const prevPartNumber = this.getPartNumber;
      const { hash, params } = this.$route;
      const { partNumber } = params;
      const newHash = hash.replace('#', '');

      const hashChanged = prevHash !== newHash;
      const productChanged = partNumber !== prevPartNumber;
      const tabIsActiveTab = this.getIsActiveProductTab(newHash);
      const tabId = tabIsActiveTab ? newHash : this.getDefaultProductTab.id;
      const setHash = tabIsActiveTab;

      if (hashChanged) this.setProductActiveTabId({ tabId, setHash });

      if (productChanged) this.updateProductData(partNumber);
    },
    handleResize() {
      this.showDesktopBanner = (window.innerWidth > breakpoints['desktop-s']);
    },
  },
  components: {
    ProductInfo,
    Gallery,
    Breadcrumbs,
    FlashMessage,
    ProductViewer,
    Subnav,
    PcRenderPromo,
    Interstitial,
  },
  beforeMount() {
    this.setBootstrap();
    this.checkCustomerGroupExpiration();
  },
  watch: {
    getPartNumber() {
      if (this.initialLoad) {
        this.initialLoad = false;
        this.initialUpdate();
      } else {
        this.updateProductRoute();
      }
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.setViewerOpen(to.name === 'ViewerPath');
    });
  },
};
